/**
 * Shared RTK Query API slice for the Bureau_notifications backend app.
 * Covers: preferences, delivery logs, system settings, test notifications.
 *
 * Portal-specific notification endpoints (list, mark read, etc.) remain in
 * bureauApiSlice / clientApiSlice / adminApiSlice to avoid coupling.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { getApiBaseUrl } from '../../../Features/API/api';
import { baseQueryWithReauth } from '../../../Features/API/baseQueryWithReauth';

const BASE_URL = getApiBaseUrl();

export const notificationApiSlice = createApi({
  reducerPath: 'notificationApi',
  baseQuery: baseQueryWithReauth(BASE_URL),
  tagTypes: [
    'NotificationPreference',
    'DeliveryLog',
    'SystemSettings',
    'UserPreferences',
    'Notifications',
    'SentNotifications',
  ],
  endpoints: (builder) => ({
    // ── Preferences (current user) ──────────────────────────────────────────
    getNotificationPreferences: builder.query({
      query: () => 'notifications/preferences/',
      providesTags: ['NotificationPreference'],
    }),

    updateNotificationPreferences: builder.mutation({
      query: (data) => ({
        url: 'notifications/preferences/',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['NotificationPreference'],
    }),

    resetNotificationPreferences: builder.mutation({
      query: () => ({
        url: 'notifications/preferences/reset/',
        method: 'POST',
      }),
      invalidatesTags: ['NotificationPreference'],
    }),

    // ── Admin: all users' preferences ──────────────────────────────────────
    getAllUserPreferences: builder.query({
      query: (params = {}) => ({
        url: 'notifications/preferences/users/',
        params,
      }),
      providesTags: ['UserPreferences'],
    }),

    resetUserPreferences: builder.mutation({
      query: (userId) => ({
        url: `notifications/preferences/users/${userId}/reset/`,
        method: 'POST',
      }),
      invalidatesTags: ['UserPreferences'],
    }),

    // ── Delivery logs (admin) ───────────────────────────────────────────────
    getDeliveryLogs: builder.query({
      query: (params = {}) => ({
        url: 'notifications/logs/',
        params,
      }),
      providesTags: ['DeliveryLog'],
    }),

    getDeliveryLogSummary: builder.query({
      query: (params = {}) => ({
        url: 'notifications/logs/',
        params: { ...params, summary: 1 },
      }),
      providesTags: ['DeliveryLog'],
    }),

    // ── System settings (admin) ─────────────────────────────────────────────
    getSystemSettings: builder.query({
      query: () => 'notifications/system-settings/',
      providesTags: ['SystemSettings'],
    }),

    updateSystemSettings: builder.mutation({
      query: (data) => ({
        url: 'notifications/system-settings/',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['SystemSettings'],
    }),

    // ── Test notification (admin) ───────────────────────────────────────────
    sendTestNotification: builder.mutation({
      query: (data) => ({
        url: 'notifications/test/',
        method: 'POST',
        body: data,
      }),
    }),

    // ── Actionable notification endpoints ─────────────────────────────────
    respondToNotification: builder.mutation({
      query: ({ notificationId, actionId, comment }) => ({
        url: `notifications/respond/${notificationId}/`,
        method: 'POST',
        body: { action_id: actionId, comment },
      }),
      invalidatesTags: ['Notifications'],
    }),

    getSentNotifications: builder.query({
      query: ({ page = 1, status } = {}) => ({
        url: 'notifications/sent/',
        params: { page, ...(status && { status }) },
      }),
      providesTags: ['SentNotifications'],
    }),

    // ── Current user's received notifications (all portals) ────────────────
    getMyNotifications: builder.query({
      query: (params = {}) => ({
        url: 'operations/notifications/',
        params,
      }),
      providesTags: ['Notifications'],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),

    markNotificationRead: builder.mutation({
      query: (notificationId) => ({
        url: `operations/notifications/${notificationId}/mark_read/`,
        method: 'POST',
      }),
      invalidatesTags: ['Notifications'],
    }),

    markAllNotificationsRead: builder.mutation({
      query: () => ({
        url: 'operations/notifications/mark_all_read/',
        method: 'POST',
      }),
      invalidatesTags: ['Notifications'],
    }),
  }),
});

export const {
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
  useResetNotificationPreferencesMutation,
  useGetAllUserPreferencesQuery,
  useResetUserPreferencesMutation,
  useGetDeliveryLogsQuery,
  useGetDeliveryLogSummaryQuery,
  useGetSystemSettingsQuery,
  useUpdateSystemSettingsMutation,
  useSendTestNotificationMutation,
  useRespondToNotificationMutation,
  useGetSentNotificationsQuery,
  useGetMyNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
} = notificationApiSlice;
